import { useState } from 'react';
import {
  Box, Typography, TextField, Button, Alert,
  IconButton, InputAdornment, CircularProgress,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { Logo } from '@/components/atoms/Logo';

const schema = z
  .object({
    password:        z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const navigate        = useNavigate();
  const [params]        = useSearchParams();
  const token           = params.get('token') ?? '';
  const [showPw, setShowPw]         = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    if (!token) { setError('Invalid or missing reset token.'); return; }
    setError('');
    setLoading(true);
    try {
      await authApi.resetPassword({ token, password: data.password, confirmPassword: data.confirmPassword });
      navigate('/login', { replace: true, state: { message: 'Password reset! Please sign in.' } });
    } catch {
      setError('Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Logo variant="full" height={44} />
      </Box>

      <Typography variant="h5" fontWeight={700} textAlign="center" mb={0.5}>
        Set new password
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3.5}>
        Choose a strong password for your account.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

      <TextField
        {...register('password')}
        label="New password"
        type={showPw ? 'text' : 'password'}
        fullWidth
        error={!!errors.password}
        helperText={errors.password?.message}
        sx={{ mb: 2 }}
        autoComplete="new-password"
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPw((p) => !p)} edge="end" size="small">
                  {showPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <TextField
        {...register('confirmPassword')}
        label="Confirm new password"
        type={showConfirm ? 'text' : 'password'}
        fullWidth
        error={!!errors.confirmPassword}
        helperText={errors.confirmPassword?.message}
        sx={{ mb: 3 }}
        autoComplete="new-password"
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm((p) => !p)} edge="end" size="small">
                  {showConfirm ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
        sx={{ py: 1.5, fontSize: '1rem' }}
      >
        {loading ? 'Updating…' : 'Reset Password'}
      </Button>

      <Box sx={{ textAlign: 'center', mt: 3 }}>
        <Link to="/login" style={{ fontSize: '0.875rem', color: 'inherit', opacity: 0.7 }}>
          Back to Sign In
        </Link>
      </Box>
    </Box>
  );
}

